import {
  type ColumnType,
  type Selectable,
  type Insertable,
  type Updateable,
} from 'kysely'
import { z } from 'zod'

import { type LegacySiteHistoryLabels } from '../../generics/LegacySitePrimitives.js'
import { cableId, type CableId } from './Cable.js'
import { legacySiteId, type LegacySiteId } from './LegacySite.js'
import { userId, type UserId } from './User.js'

/** Identifier type for public.legacy_site_cable_history_ */
export type LegacySiteCableHistoryId = string & {
  __brand: 'LegacySiteCableHistoryId'
}

/** Represents the table public.legacy_site_cable_history_ */
export default interface LegacySiteCableHistoryTable {
  /** Database type: pg_catalog.uuid */
  id: ColumnType<
    LegacySiteCableHistoryId,
    LegacySiteCableHistoryId | undefined,
    LegacySiteCableHistoryId
  >

  /** Database type: pg_catalog.int4 */
  siteId: ColumnType<LegacySiteId, LegacySiteId, LegacySiteId>

  /** Database type: pg_catalog.int4 */
  cableId: ColumnType<CableId, CableId, CableId>

  /** Database type: pg_catalog.timestamptz */
  startedAt: ColumnType<Date, Date | string, Date | string>

  /** Database type: pg_catalog.timestamptz */
  endedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.int4[] */
  decode: ColumnType<number[] | null, number[] | null, number[] | null>

  /** Database type: pg_catalog.text */
  dataType: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.jsonb */
  labels: ColumnType<
    LegacySiteHistoryLabels | null,
    LegacySiteHistoryLabels | null,
    LegacySiteHistoryLabels | null
  >

  /** Database type: pg_catalog.timestamptz */
  createdAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  createdBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  updatedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  updatedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  deletedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  deletedBy: ColumnType<UserId | null, UserId | null, UserId | null>
}

export type LegacySiteCableHistory = Selectable<LegacySiteCableHistoryTable>

export type LegacySiteCableHistoryInitializer =
  Insertable<LegacySiteCableHistoryTable>

export type LegacySiteCableHistoryMutator =
  Updateable<LegacySiteCableHistoryTable>

export const legacySiteCableHistoryId =
  z.string() as unknown as z.Schema<LegacySiteCableHistoryId>

export const legacySiteCableHistory = z.object({
  id: legacySiteCableHistoryId,
  siteId: legacySiteId,
  cableId: cableId,
  startedAt: z.coerce.date(),
  endedAt: z.coerce.date().nullable().optional(),
  decode: z.number().array().nullable().optional(),
  dataType: z.string().nullable().optional(),
  labels: z.unknown().nullable().optional(),
  createdAt: z.coerce.date().nullable().optional(),
  createdBy: userId.nullable().optional(),
  updatedAt: z.coerce.date().nullable().optional(),
  updatedBy: userId.nullable().optional(),
  deletedAt: z.coerce.date().nullable().optional(),
  deletedBy: userId.nullable().optional(),
}) as unknown as z.Schema<LegacySiteCableHistory>

export const legacySiteCableHistoryInitializer = z.object({
  id: legacySiteCableHistoryId.optional(),
  siteId: legacySiteId,
  cableId: cableId,
  startedAt: z.coerce.date(),
  endedAt: z.coerce.date().optional().nullable(),
  decode: z.number().array().optional().nullable(),
  dataType: z.string().optional().nullable(),
  labels: z.unknown().optional().nullable(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
}) as unknown as z.Schema<LegacySiteCableHistoryInitializer>

export const legacySiteCableHistoryMutator = z.object({
  id: legacySiteCableHistoryId.optional(),
  siteId: legacySiteId.optional(),
  cableId: cableId.optional(),
  startedAt: z.coerce.date().optional(),
  endedAt: z.coerce.date().optional().nullable(),
  decode: z.number().array().optional().nullable(),
  dataType: z.string().optional().nullable(),
  labels: z.unknown().optional().nullable(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
}) as unknown as z.Schema<LegacySiteCableHistoryMutator>
